import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Sparkles, Loader2, ArrowRight, Send, Bot, User as UserIcon } from "lucide-react";

const QUICK_ACTIONS = [
  { key: "margin_up_10", label: "+10% precios", color: "border-green-300 text-green-700 hover:bg-green-50" },
  { key: "margin_up_5", label: "+5% precios", color: "border-green-300 text-green-700 hover:bg-green-50" },
  { key: "margin_down_5", label: "-5% precios", color: "border-orange-300 text-orange-700 hover:bg-orange-50" },
  { key: "margin_down_10", label: "-10% precios", color: "border-orange-300 text-orange-700 hover:bg-orange-50" },
  { key: "round_prices", label: "Redondear a €", color: "border-slate-300 text-slate-700 hover:bg-slate-50" },
  { key: "iva_10", label: "IVA reducido 10%", color: "border-blue-300 text-blue-700 hover:bg-blue-50" },
  { key: "iva_21", label: "IVA 21%", color: "border-blue-300 text-blue-700 hover:bg-blue-50" },
  { key: "iva_0", label: "Sin IVA", color: "border-blue-300 text-blue-700 hover:bg-blue-50" },
  { key: "shorter_delivery", label: "Plazo más corto +20%", color: "border-red-300 text-red-700 hover:bg-red-50" },
  { key: "longer_delivery", label: "Plazo más largo -5%", color: "border-slate-300 text-slate-700 hover:bg-slate-50" },
  { key: "more_detail", label: "Más detalle", color: "border-slate-300 text-slate-700 hover:bg-slate-50" },
  { key: "less_detail", label: "Menos detalle", color: "border-slate-300 text-slate-700 hover:bg-slate-50" },
];

export default function BudgetAiChat({ budgetId, history = [], onApplied, status }) {
  const [busy, setBusy] = useState(false);
  const [input, setInput] = useState("");
  const [activeQuick, setActiveQuick] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [history]);

  const isDraft = !status || status === "draft";

  const run = async ({ instruction = "", quick_action = null }) => {
    if (!isDraft) {
      toast.error("La IA sólo edita presupuestos en borrador");
      return;
    }
    if (!instruction && !quick_action) return;
    setBusy(true);
    setActiveQuick(quick_action);
    try {
      const { data } = await api.post(`/budgets/${budgetId}/refine`, { instruction, quick_action });
      toast.success(data.ai_message || "Cambio aplicado");
      setInput("");
      onApplied?.(data.budget);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error con la IA");
    } finally {
      setBusy(false);
      setActiveQuick(null);
    }
  };

  return (
    <div className="bg-slate-900 text-white flex flex-col" data-testid="budget-ai-chat">
      <div className="p-4 border-b border-slate-800 flex items-center gap-2">
        <div className="h-8 w-8 bg-orange-600 flex items-center justify-center">
          <Sparkles className="w-4 h-4" />
        </div>
        <div className="flex-1">
          <div className="font-display font-bold">Asistente IA</div>
          <div className="text-xs text-slate-400">Pídele cambios al presupuesto</div>
        </div>
      </div>

      {/* History */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[300px]" data-testid="ai-chat-history">
        {history.length === 0 && (
          <div className="text-xs text-slate-500 text-center py-4">
            Aún no has hablado con la IA. Pulsa una acción rápida abajo o escribe lo que quieras cambiar.
          </div>
        )}
        {history.map((m, i) => (
          <div key={i} className={`flex gap-2 text-sm ${m.role === "user" ? "flex-row-reverse" : ""}`}>
            <div className={`h-6 w-6 flex-shrink-0 flex items-center justify-center ${m.role === "user" ? "bg-orange-600" : "bg-slate-700"}`}>
              {m.role === "user" ? <UserIcon className="w-3 h-3" /> : <Bot className="w-3 h-3" />}
            </div>
            <div className={`px-3 py-2 max-w-[80%] ${m.role === "user" ? "bg-orange-600 text-white" : "bg-slate-800 text-slate-100"}`}>
              {m.text}
              {m.quick_action && <div className="text-[10px] text-orange-200 mt-1 uppercase tracking-wider">acción: {m.quick_action}</div>}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex gap-2 text-sm">
            <div className="h-6 w-6 bg-slate-700 flex items-center justify-center"><Bot className="w-3 h-3" /></div>
            <div className="px-3 py-2 bg-slate-800 inline-flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Pensando…</div>
          </div>
        )}
      </div>

      {/* Quick actions */}
      <div className="p-3 border-t border-slate-800">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Acciones rápidas</div>
        <div className="flex flex-wrap gap-1.5">
          {QUICK_ACTIONS.map((a) => (
            <button
              key={a.key}
              disabled={busy || !isDraft}
              onClick={() => run({ quick_action: a.key })}
              data-testid={`ai-quick-${a.key}`}
              className={`text-xs px-2 py-1 border bg-white/5 hover:bg-white/10 transition-colors disabled:opacity-30 ${activeQuick === a.key ? "ring-2 ring-orange-500" : ""}`}
            >
              {activeQuick === a.key && busy ? <Loader2 className="w-3 h-3 animate-spin inline" /> : a.label}
            </button>
          ))}
        </div>
      </div>

      {/* Free-form input */}
      <div className="p-3 border-t border-slate-800">
        <div className="flex gap-2 items-end">
          <textarea
            data-testid="ai-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) run({ instruction: input });
            }}
            placeholder="Ej: 'Añade una partida de albañilería de 4h' · 'Cambia plazo a 5 días' · 'Pon garantía de 24 meses'"
            rows={2}
            disabled={busy || !isDraft}
            className="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 focus:border-orange-600 outline-none text-sm placeholder-slate-500"
          />
          <button
            data-testid="ai-send"
            disabled={busy || !input.trim() || !isDraft}
            onClick={() => run({ instruction: input })}
            className="h-10 px-3 bg-orange-600 hover:bg-orange-700 disabled:opacity-40 inline-flex items-center gap-1 font-semibold text-sm"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
        <div className="text-[10px] text-slate-500 mt-1">Ctrl/Cmd + Enter para enviar</div>
      </div>

      {!isDraft && (
        <div className="bg-yellow-900/40 text-yellow-200 text-xs px-3 py-2 border-t border-yellow-800">
          Este presupuesto ya no es borrador — pasa a estado "Borrador" para editarlo con IA.
        </div>
      )}
    </div>
  );
}
